import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { CompanyBrand } from '../models/companyBrand';

const apiUrl = process.env.REACT_APP_BASIX_API || '';

const BRAND_CACHE_KEY = 'basix-brand-cache';

/**
 * Last known brand, persisted locally so the app boots straight into the
 * company's look instead of flashing the stock Basix theme while the
 * settings request is in flight. Refreshed after every successful fetch.
 */
export const readBrandCache = (): CompanyBrand | undefined => {
  try {
    const raw = window.localStorage.getItem(BRAND_CACHE_KEY);
    return raw ? (JSON.parse(raw) as CompanyBrand) : undefined;
  } catch {
    return undefined;
  }
};

const getBrand = async (): Promise<CompanyBrand> => {
  const { data } = await axios.get(`${apiUrl}settings/Brand`);
  return data;
};

const useCompanyBrand = () => {
  const queryClient = useQueryClient();
  const query = useQuery('companyBrand', getBrand, {
    initialData: readBrandCache,
    // item 860 — cache is a paint hint only; always revalidate so brand edits made in
    // another session (or before this load) land without waiting out staleTime
    initialDataUpdatedAt: 0,
    onSuccess: (data) => {
      try {
        window.localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(data));
      } catch {
        // storage full/blocked — just skip the cache
      }
    },
  });

  const mutation = useMutation(
    (brand: CompanyBrand) => axios.post(`${apiUrl}settings/Brand`, brand),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('companyBrand');
        // item 860 — the chrome-only mirror of the same rows (Outside Sales portal)
        queryClient.invalidateQueries('brandChrome');
      },
    },
  );

  return {
    ...query,
    brand: query.data,
    tryUpdate: mutation.mutateAsync,
    isSaving: mutation.isLoading,
  };
};

export default useCompanyBrand;
